import { useState, useEffect, useCallback } from "react";
import { CURRENCIES, getPaymentStatusColor, fmtCurrency } from "./utils.js";
import {
  getAllInvoices,
  getPaymentStatus as getPaymentStatusFromDB,
  deleteInvoice,
  exportFullBackup,
  exportAuditData,
  downloadExport
} from "./db/index.js";
import { tierHasFeature, FEATURES } from "./db/features/featureList.js";

export default function InvoiceHistory({
  G,
  onView,
  onDelete,
  onAddPayment,
  brand,
  settings,
  user,
  refreshTrigger = 0
}) {
  // Local state
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [displayLimit, setDisplayLimit] = useState(50);
  const [deleteModal, setDeleteModal] = useState(null);
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState("full"); // "full" or "audit"
  const [exportFormat, setExportFormat] = useState("json"); // "json" or "csv"
  const [exportFlattenLines, setExportFlattenLines] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Feature flags
  const hasCloudDB = user && tierHasFeature(user.tierLevel, FEATURES.CLOUD_DATABASE);
  const hasPaymentTracking = user && tierHasFeature(user.tierLevel, FEATURES.PAYMENT_TRACKING);
  const hasSearch = user && tierHasFeature(user.tierLevel, FEATURES.SEARCH);
  const hasJsonExport = user && tierHasFeature(user.tierLevel, FEATURES.DATA_EXPORT_GDPR);
  const hasCsvExport = user && tierHasFeature(user.tierLevel, FEATURES.CSV_EXPORT);
  const hasPaymentStatusFilter = user && tierHasFeature(user.tierLevel, FEATURES.FILTER_BY_PAYMENT_STATUS);

  // Fetch invoices from database with payment enrichment
  const fetchInvoices = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Get all active invoices from DB
      const invoicesFromDB = await getAllInvoices({
        status: "active",
        includeDeleted: false,
        includeVoided: false
      });

      // Enrich each invoice with payment data
      const enriched = await Promise.all(
        invoicesFromDB.map(async (inv) => {
          try {
            const paymentStatus = await getPaymentStatusFromDB(inv.id);

            return {
              ...inv,
              amountPaid: paymentStatus.amountPaid,
              paymentStatus: paymentStatus.status,
              outstanding: paymentStatus.outstanding
            };
          } catch (error) {
            console.error(`Failed to enrich invoice ${inv.id}:`, error);
            // Return invoice without payment data if enrichment fails
            return {
              ...inv,
              amountPaid: 0,
              paymentStatus: "unpaid",
              outstanding: inv.grandTotal
            };
          }
        })
      );

      setInvoices(enriched);
    } catch (error) {
      // Handle permission errors gracefully (FREE tier)
      if (error.message?.includes('permission') || 
          error.message?.includes('feature') ||
          error.message?.includes('requires')) {
        console.warn("Feature not available for current tier:", error.message);
        setInvoices([]);
      } else {
        console.error("Failed to load invoices:", error);
        setInvoices([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load on mount and when refreshTrigger changes
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices, refreshTrigger]);

  // Utility functions
  const getPaymentStatus = (inv) => {
    return inv.paymentStatus || "unpaid";
  };

  const getOutstanding = (inv) => {
    return inv.outstanding !== undefined ? inv.outstanding : (inv.grandTotal - (inv.amountPaid || 0));
  };

  const currencySymbol = brand.currency === "NONE"
    ? ""
    : brand.currency === "CUSTOM"
      ? (brand.customCurrencySymbol || "?")
      : (CURRENCIES.find(c => c.value === (brand.currency || "NONE"))?.symbol || "");

  // Format currency using settings
  const fmt = (n) => {
    return fmtCurrency(
      n,
      currencySymbol,
      settings.currencyFormat || "symbol-before-space",
      brand.currency || "NONE",
      settings.amountGrouping || "international",
      settings.amountSeparator || "comma-dot",
      settings.amountDecimals !== false
    );
  };

  // Plain number formatter for table columns
  const fmtInvoicePlain = (amount, invoice) => {
    return fmtCurrency(
      amount,
      "",
      "no-symbol",
      "",
      invoice.amountGrouping || invoice.formatSettings?.amountGrouping || "international",
      invoice.amountSeparator || invoice.formatSettings?.amountSeparator || "comma-dot",
      invoice.amountDecimals !== undefined ? invoice.amountDecimals : (invoice.formatSettings?.amountDecimals !== false)
    );
  };

  // Filter invoices
  const filtered = invoices.filter((inv) => {
    const matchesSearch = hasSearch 
      ? (inv.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
         inv.invNo?.toLowerCase().includes(search.toLowerCase()))
      : true;
    
    const invDate = new Date(inv.invDate);
    const matchesFrom = fromDate ? invDate >= new Date(fromDate) : true;
    const matchesTo = toDate ? invDate <= new Date(toDate) : true;
    
    const status = getPaymentStatus(inv);
    const matchesPaymentStatus = (paymentStatusFilter === "all" || !hasPaymentStatusFilter) 
      ? true 
      : status === paymentStatusFilter;
    
    return matchesSearch && matchesFrom && matchesTo && matchesPaymentStatus;
  });

  // Sort invoices
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortColumn) {
      case "date":
        aVal = new Date(a.invDate).getTime();
        bVal = new Date(b.invDate).getTime();
        break;
      case "invoice":
        aVal = a.invNo.toLowerCase();
        bVal = b.invNo.toLowerCase();
        break;
      case "customer":
        aVal = (a.customer?.name || "").toLowerCase();
        bVal = (b.customer?.name || "").toLowerCase();
        break;
      case "total":
        aVal = a.grandTotal || 0;
        bVal = b.grandTotal || 0;
        break;
      case "paid":
        aVal = a.amountPaid || 0;
        bVal = b.amountPaid || 0;
        break;
      case "due":
        aVal = getOutstanding(a);
        bVal = getOutstanding(b);
        break;
      default:
        return 0;
    }
    
    if (sortDirection === "asc") {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Apply display limit
  const displayed = sorted.slice(0, displayLimit);
  const hasMore = sorted.length > displayLimit;

  // Check if any filters are active
  const hasFilters = search || fromDate || toDate;

  // Count unique customers
  const uniqueCustomers = new Set(filtered.map(inv => inv.customer?.name).filter(Boolean)).size;

  const totalValue = filtered.reduce((s, inv) => s + (inv.grandTotal || 0), 0);
  const totalCollected = filtered.reduce((s, inv) => s + (inv.amountPaid || 0), 0);
  const totalOutstanding = filtered.reduce((s, inv) => s + getOutstanding(inv), 0);
  
  const clearFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
    setDisplayLimit(50);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{
        color: "#FFFFFF",
        fontSize: "14px",
        marginLeft: "4px",
        opacity: 0.5,
        fontWeight: 600
      }}>⇅</span>;
    }
    return sortDirection === "asc"
      ? <span style={{ color: "#FFFFFF", fontSize: "14px", marginLeft: "4px", fontWeight: 700 }}>▲</span>
      : <span style={{ color: "#FFFFFF", fontSize: "14px", marginLeft: "4px", fontWeight: 700 }}>▼</span>;
  };

  const handleDeleteClick = (invoice) => {
    setDeleteModal({ invoice });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal?.invoice) {
      try {
        // Soft delete in database
        await deleteInvoice(deleteModal.invoice.id);
        
        // Call parent's onDelete callback (if it needs to do anything)
        if (onDelete) {
          onDelete(deleteModal.invoice);
        }
        
        // Refresh the list
        await fetchInvoices();
        
        setDeleteModal(null);
      } catch (error) {
        console.error("Failed to delete invoice:", error);
        alert("Failed to delete invoice. Please try again.");
      }
    }
  };

  // Wrap onAddPayment to refresh after payment added
  const handleAddPayment = async (invoice) => {
    if (onAddPayment) {
      await onAddPayment(invoice);
      // Refresh to show updated payment status
      await fetchInvoices();
    }
  };

  // Handle export
  const handleExport = async () => {
    if (!hasJsonExport) return;
    
    setExporting(true);
    try {
      // Build filters from current state
      const filters = {};
      if (fromDate) filters.fromDate = fromDate;
      if (toDate) filters.toDate = toDate;
      if (search && hasSearch) filters.customer = search;
      if (paymentStatusFilter !== "all" && hasPaymentStatusFilter) {
        filters.paymentStatus = paymentStatusFilter;
      }
      
      // Build options
      const options = {
        format: exportFormat,
        flattenLines: exportFlattenLines
      };
      
      // Call appropriate export function
      let exportData;
      if (exportType === "audit") {
        exportData = await exportAuditData(filters, options);
      } else {
        exportData = await exportFullBackup(filters, options);
      }
      
      // Download file
      downloadExport(exportData);
      
      // Close modal
      setShowExportModal(false);
      
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Loading brand/settings check
  if (!brand || !settings) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        flexDirection: "column",
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⏳</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: G.ink }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "24px",
            maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Delete Invoice?
            </div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 16, lineHeight: 1.5 }}>
              Delete invoice <strong>{deleteModal.invoice.invNo}</strong>?
              <br /><br />
              Deleting this invoice will exclude:
              <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 20 }}>
                <li>Invoice amount <strong>({fmt(deleteModal.invoice.grandTotal)})</strong> from Total Revenue</li>
                <li>Received amount <strong>({fmt(deleteModal.invoice.amountPaid || 0)})</strong> from Collected</li>
                <li>Outstanding amount <strong>({fmt(getOutstanding(deleteModal.invoice))})</strong> from Due</li>
              </ul>
              It will be hidden from history and reports but kept in records for audit purposes.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteModal(null)}
                style={{
                  padding: "8px 16px",
                  border: `1px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface2,
                  color: G.ink,
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: 7,
                  background: G.danger,
                  color: "#FFFFFF",
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "28px",
            maxWidth: 520, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Export Invoice Data
            </div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Exporting <strong>{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</strong>
              {hasFilters && " (filtered)"}
            </div>

            {/* Export Type */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                fontSize: 11, fontWeight: 600, color: G.muted,
                textTransform: "uppercase", letterSpacing: .6,
                display: "block", marginBottom: 8
              }}>
                Export Type
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setExportType("full")}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1.5px solid ${exportType === "full" ? G.accent : G.border}`,
                    borderRadius: 7,
                    background: exportType === "full" ? G.accentBg : G.surface2,
                    color: exportType === "full" ? G.accent : G.ink,
                    cursor: "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: exportType === "full" ? 600 : 400,
                    transition: "all .15s"
                  }}
                >
                  Full Backup
                </button>
                <button
                  onClick={() => setExportType("audit")}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1.5px solid ${exportType === "audit" ? G.accent : G.border}`,
                    borderRadius: 7,
                    background: exportType === "audit" ? G.accentBg : G.surface2,
                    color: exportType === "audit" ? G.accent : G.ink,
                    cursor: "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: exportType === "audit" ? 600 : 400,
                    transition: "all .15s"
                  }}
                >
                  Audit Export
                </button>
              </div>
              <div style={{ fontSize: 11, color: G.muted, marginTop: 6 }}>
                {exportType === "full" 
                  ? "Complete data with all snapshots and metadata"
                  : "Simplified format for accountants (without snapshots)"}
              </div>
            </div>

            {/* Format Selection */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                fontSize: 11, fontWeight: 600, color: G.muted,
                textTransform: "uppercase", letterSpacing: .6,
                display: "block", marginBottom: 8
              }}>
                Format
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setExportFormat("json")}
                  disabled={!hasJsonExport}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1.5px solid ${exportFormat === "json" ? G.accent : G.border}`,
                    borderRadius: 7,
                    background: exportFormat === "json" ? G.accentBg : G.surface2,
                    color: exportFormat === "json" ? G.accent : G.ink,
                    cursor: hasJsonExport ? "pointer" : "not-allowed",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: exportFormat === "json" ? 600 : 400,
                    opacity: hasJsonExport ? 1 : 0.4,
                    filter: hasJsonExport ? 'none' : 'grayscale(100%)',
                    transition: "all .15s"
                  }}
                >
                  JSON
                </button>
                <button
                  onClick={() => setExportFormat("csv")}
                  disabled={!hasCsvExport}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1.5px solid ${exportFormat === "csv" ? G.accent : G.border}`,
                    borderRadius: 7,
                    background: exportFormat === "csv" ? G.accentBg : G.surface2,
                    color: exportFormat === "csv" ? G.accent : G.ink,
                    cursor: hasCsvExport ? "pointer" : "not-allowed",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: exportFormat === "csv" ? 600 : 400,
                    opacity: hasCsvExport ? 1 : 0.4,
                    filter: hasCsvExport ? 'none' : 'grayscale(100%)',
                    transition: "all .15s"
                  }}
                >
                  CSV
                </button>
              </div>
              {!hasCsvExport && exportFormat === "csv" && (
                <div style={{ fontSize: 11, color: "#EF4444", marginTop: 6 }}>
                  CSV export requires Exclusive tier
                </div>
              )}
            </div>

            {/* Flatten Lines Option */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                padding: "12px 14px",
                border: `1.5px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface2,
                transition: "all .15s"
              }}
              onMouseEnter={e => e.target.style.borderColor = G.accent}
              onMouseLeave={e => e.target.style.borderColor = G.border}
              >
                <input
                  type="checkbox"
                  checked={exportFlattenLines}
                  onChange={(e) => setExportFlattenLines(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer" }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: G.ink }}>
                    Flatten Line Items
                  </div>
                  <div style={{ fontSize: 11, color: G.muted }}>
                    One row per line item (better for spreadsheets)
                  </div>
                </div>
              </label>
            </div>

            {/* Active Filters Display */}
            {hasFilters && (
              <div style={{
                padding: "12px 14px",
                background: G.accentBg,
                border: `1px solid ${G.accent}40`,
                borderRadius: 7,
                marginBottom: 20
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: G.accent, marginBottom: 6 }}>
                  ACTIVE FILTERS
                </div>
                <div style={{ fontSize: 12, color: G.ink }}>
                  {search && <div>• Search: "{search}"</div>}
                  {fromDate && <div>• From: {new Date(fromDate).toLocaleDateString()}</div>}
                  {toDate && <div>• To: {new Date(toDate).toLocaleDateString()}</div>}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  border: `1px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface2,
                  color: G.ink,
                  cursor: exporting ? "not-allowed" : "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  opacity: exporting ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting || (exportFormat === "csv" && !hasCsvExport)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: 7,
                  background: (exportFormat === "csv" && !hasCsvExport) ? G.border : G.accent,
                  color: "#FFFFFF",
                  cursor: (exporting || (exportFormat === "csv" && !hasCsvExport)) ? "not-allowed" : "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: (exporting || (exportFormat === "csv" && !hasCsvExport)) ? 0.5 : 1
                }}
              >
                {exporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: G.ink, marginBottom: 4 }}>
            🕒 Invoice History
          </h2>
          <div style={{ color: G.muted, fontSize: 13 }}>
            {loading ? "Loading..." : `${invoices.length} invoice${invoices.length !== 1 ? "s" : ""} total`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Refresh Button */}
          <button
            onClick={fetchInvoices}
            disabled={loading}
            style={{
              background: loading ? G.surface2 : G.accentBg,
              color: loading ? G.muted : G.accent,
              border: `1.5px solid ${loading ? G.border : G.accent}40`,
              borderRadius: 7,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 500,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "'Sora', sans-serif",
              transition: "all 0.15s"
            }}
            onMouseEnter={e => {
              if (!loading) {
                e.target.style.background = G.accent;
                e.target.style.color = G.accentFg;
                e.target.style.border = "1.5px solid transparent";
              }
            }}
            onMouseLeave={e => {
              if (!loading) {
                e.target.style.background = G.accentBg;
                e.target.style.color = G.accent;
                e.target.style.border = `1.5px solid ${G.accent}40`;
              }
            }}
          >
            {loading ? "⏳ Loading..." : "🔄 Refresh"}
          </button>

          {/* Export Button */}
          {invoices.length > 0 && (
            <button
              onClick={() => setShowExportModal(true)}
              disabled={!hasJsonExport}
              style={{
                background: hasJsonExport ? G.ink : G.surface2,
                color: hasJsonExport ? G.surface : G.muted,
                border: hasJsonExport ? "none" : `1px solid ${G.border}`,
                borderRadius: 7,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 500,
                cursor: hasJsonExport ? "pointer" : "not-allowed",
                fontFamily: "'Sora', sans-serif",
                opacity: hasJsonExport ? 1 : 0.4,
                filter: hasJsonExport ? 'none' : 'grayscale(100%)',
                transition: "all 0.15s"
              }}
            >
              ⬇ Export Data
            </button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          {hasFilters && (
            <button onClick={clearFilters} style={{
              background: "transparent",
              border: "none",
              color: G.accent,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'Sora', sans-serif",
              textDecoration: "underline",
              padding: 0
            }}>
              Clear all filters
            </button>
          )}
        </div>

        {/* Filter Cards Grid (3 columns - removed Product filter) */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
          
          {/* Search Card */}
          <div style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 9, padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: 12 }}>🔍</span> Search
            </label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Customer Name / Invoice Number"
              disabled={loading || !hasSearch}
              style={{
                width: "100%", 
                padding: "8px 10px", 
                border: `1.5px solid ${G.border}`, 
                borderRadius: 6,
                fontSize: 13, 
                fontFamily: "'Sora', sans-serif", 
                outline: "none", 
                color: G.ink, 
                background: G.surface,
                transition: "border-color 0.15s",
                opacity: hasSearch ? 1 : 0.4,
                cursor: hasSearch ? "text" : "not-allowed"
              }}
              onFocus={e => hasSearch && (e.target.style.borderColor = G.accent)}
              onBlur={e => e.target.style.borderColor = G.border}
            />
          </div>

          {/* From Date Card */}
          <div style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 9, padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: 12 }}>📅</span> From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              disabled={loading}
              style={{
                width: "100%", padding: "8px 10px", border: `1.5px solid ${G.border}`, borderRadius: 6,
                fontSize: 13, fontFamily: "'Sora', sans-serif", outline: "none", color: G.ink, background: G.surface,
                cursor: "pointer", textTransform: "uppercase", transition: "border-color 0.15s"
              }}
              onFocus={e => e.target.style.borderColor = G.accent}
              onBlur={e => e.target.style.borderColor = G.border}
            />
          </div>

          {/* To Date Card */}
          <div style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 9, padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: G.muted, letterSpacing: .5, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: 12 }}>📅</span> To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              disabled={loading}
              style={{
                width: "100%", padding: "8px 10px", border: `1.5px solid ${G.border}`, borderRadius: 6,
                fontSize: 13, fontFamily: "'Sora', sans-serif", outline: "none", color: G.ink, background: G.surface,
                cursor: "pointer", textTransform: "uppercase", transition: "border-color 0.15s"
              }}
              onFocus={e => e.target.style.borderColor = G.accent}
              onBlur={e => e.target.style.borderColor = G.border}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: "center",
          padding: "60px 0",
          border: `2px dashed ${G.border}`,
          borderRadius: 12,
          color: G.muted
        }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Loading invoices...</div>
          <div style={{ fontSize: 13 }}>Fetching data from database</div>
        </div>
      )}

      {/* Empty states */}
      {!loading && invoices.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", border: `2px dashed ${G.border}`, borderRadius: 12, color: G.muted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>No invoices yet</div>
          <div style={{ fontSize: 13 }}>Generate your first invoice from the Billing screen</div>
        </div>
      ) : !loading && filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", border: `2px dashed ${G.border}`, borderRadius: 12, color: G.muted }}>
          <div style={{ fontSize: 13 }}>No invoices match your filters</div>
        </div>
      ) : !loading && (
        <>
          {/* Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 18 }}>
            {/* Showing Invoices Card */}
            <div style={{
              background: G.surface,
              border: `1px solid ${G.border}`,
              borderRadius: 10,
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Showing
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: G.ink, lineHeight: 1 }}>
                {displayed.length}
                {filtered.length > displayed.length && (
                  <span style={{ fontSize: 14, fontWeight: 500, color: G.muted, marginLeft: 4 }}>
                    of {filtered.length}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: G.muted }}>
                Invoice{filtered.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Total Card */}
            <div style={{
              background: "linear-gradient(135deg, #EBF4FF 0%, #D6E9FF 100%)",
              border: `1px solid ${G.accent}33`,
              borderRadius: 10,
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#1E3A8A", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Total Invoiced
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: G.accent, lineHeight: 1 }}>
                {fmt(totalValue)}
              </div>
              <div style={{ fontSize: 12, color: "#1E3A8A" }}>
                All invoices
              </div>
            </div>

            {/* Collected Card */}
            <div style={{
              background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
              border: "1px solid rgba(34, 197, 94, 0.2)",
              borderRadius: 10,
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#166534", textTransform: "uppercase", letterSpacing: 0.5 }}>
                ✓ Collected
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#22C55E", lineHeight: 1 }}>
                {fmt(totalCollected)}
              </div>
              <div style={{ fontSize: 12, color: "#166534" }}>
                {((totalCollected / totalValue) * 100 || 0).toFixed(1)}% received
              </div>
            </div>

            {/* Outstanding Card */}
            <div style={{
              background: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              borderRadius: 10,
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#991B1B", textTransform: "uppercase", letterSpacing: 0.5 }}>
                ⏳ Outstanding
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#EF4444", lineHeight: 1 }}>
                {fmt(totalOutstanding)}
              </div>
              <div style={{ fontSize: 12, color: "#991B1B" }}>
                {((totalOutstanding / totalValue) * 100 || 0).toFixed(1)}% pending
              </div>
            </div>

            {/* Customers Card */}
            <div style={{
              background: G.surface,
              border: `1px solid ${G.border}`,
              borderRadius: 10,
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 6
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Customers
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: G.ink, lineHeight: 1 }}>
                {uniqueCustomers}
              </div>
              <div style={{ fontSize: 12, color: G.muted }}>
                Unique clients
              </div>
            </div>
          </div>

          {/* Payment Status Filter */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[
              { value: "all", label: "All" },
              { value: "paid", label: "Paid" },
              { value: "partial", label: "Partially Paid" },
              { value: "unpaid", label: "Unpaid" }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => {
                  setPaymentStatusFilter(value);
                  setDisplayLimit(50);
                }}
                disabled={loading || !hasPaymentStatusFilter}
                style={{
                  padding: "8px 16px",
                  border: `1.5px solid ${paymentStatusFilter === value ? G.accent : G.border}`,
                  borderRadius: 7,
                  background: paymentStatusFilter === value ? G.accentBg : G.surface,
                  color: paymentStatusFilter === value ? G.accent : G.ink,
                  fontSize: 13,
                  fontWeight: paymentStatusFilter === value ? 600 : 400,
                  cursor: (loading || !hasPaymentStatusFilter) ? "not-allowed" : "pointer",
                  fontFamily: "'Sora', sans-serif",
                  transition: "all .15s",
                  opacity: hasPaymentStatusFilter ? 1 : 0.4,
                  filter: hasPaymentStatusFilter ? 'none' : 'grayscale(100%)'
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Invoice Table */}
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, overflow: "hidden", marginBottom: hasMore ? 14 : 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: G.ink }}>
                  <th style={{ color: G.surface, padding: "10px 14px", textAlign: "center", fontSize: 10, fontWeight: 600, letterSpacing: .6, textTransform: "uppercase", width: 40 }}>●</th>
                  
                  {/* Sortable columns */}
                  <th
                    onClick={() => handleSort("date")}
                    onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.target.style.background = "transparent"}
                    style={{
                      color: G.surface, padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 600,
                      letterSpacing: .6, textTransform: "uppercase", cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s"
                    }}
                  >
                    Date <SortIcon column="date" />
                  </th>
                  
                  <th
                    onClick={() => handleSort("invoice")}
                    onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.target.style.background = "transparent"}
                    style={{
                      color: G.surface, padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 600,
                      letterSpacing: .6, textTransform: "uppercase", cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s"
                    }}
                  >
                    Invoice # <SortIcon column="invoice" />
                  </th>
                  
                  <th
                    onClick={() => handleSort("customer")}
                    onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.target.style.background = "transparent"}
                    style={{
                      color: G.surface, padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 600,
                      letterSpacing: .6, textTransform: "uppercase", cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s"
                    }}
                  >
                    Customer <SortIcon column="customer" />
                  </th>
                  
                  <th
                    onClick={() => handleSort("total")}
                    onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.target.style.background = "transparent"}
                    style={{
                      color: G.surface, padding: "10px 14px", textAlign: "right", fontSize: 10, fontWeight: 600,
                      letterSpacing: .6, textTransform: "uppercase", cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s"
                    }}
                  >
                    Total <SortIcon column="total" />
                  </th>
                  
                  <th
                    onClick={() => handleSort("paid")}
                    onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.target.style.background = "transparent"}
                    style={{
                      color: G.surface, padding: "10px 14px", textAlign: "right", fontSize: 10, fontWeight: 600,
                      letterSpacing: .6, textTransform: "uppercase", cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s"
                    }}
                  >
                    Paid <SortIcon column="paid" />
                  </th>
                  
                  <th
                    onClick={() => handleSort("due")}
                    onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                    onMouseLeave={e => e.target.style.background = "transparent"}
                    style={{
                      color: G.surface, padding: "10px 14px", textAlign: "right", fontSize: 10, fontWeight: 600,
                      letterSpacing: .6, textTransform: "uppercase", cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s"
                    }}
                  >
                    Due <SortIcon column="due" />
                  </th>
                  
                  <th style={{ color: G.surface, padding: "10px 14px", textAlign: "center", fontSize: 10, fontWeight: 600, letterSpacing: .6, textTransform: "uppercase" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {displayed.map((inv, i) => {
                  const status = getPaymentStatus(inv);
                  const statusColor = getPaymentStatusColor(status);
                  const outstanding = getOutstanding(inv);
                  
                  return (
                    <tr key={inv.id || i} style={{ borderTop: `1px solid ${G.border}`, background: i % 2 === 0 ? G.surface : G.surface2, transition: "background .1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = G.accentBg}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? G.surface : G.surface2}
                    >
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <div style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: statusColor,
                          margin: "0 auto"
                        }} />
                      </td>
                      
                      <td style={{ padding: "12px 14px", fontSize: 13, color: G.muted, whiteSpace: "nowrap" }}>
                        {new Date(inv.invDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      
                      <td style={{ padding: "12px 14px", fontWeight: 600, fontSize: 13, color: G.ink }}>{inv.invNo}</td>
                      
                      <td style={{ padding: "12px 14px", fontWeight: 500, fontSize: 13 }}>{inv.customer?.name || "—"}</td>
                      
                      <td style={{ padding: "12px 14px", fontWeight: 700, color: G.accent, whiteSpace: "nowrap", textAlign: "right" }}>
                        {fmtInvoicePlain(inv.grandTotal, inv)}
                      </td>
                      
                      <td style={{ padding: "12px 14px", fontWeight: 600, color: "#22C55E", whiteSpace: "nowrap", textAlign: "right" }}>
                        {inv.amountPaid > 0 ? fmtInvoicePlain(inv.amountPaid, inv) : "—"}
                      </td>
                      
                      <td style={{ padding: "12px 14px", fontWeight: 600, color: "#EF4444", whiteSpace: "nowrap", textAlign: "right" }}>
                        {outstanding > 0 ? fmtInvoicePlain(outstanding, inv) : "—"}
                      </td>
                      
                      <td style={{ padding: "12px 14px", verticalAlign: "middle", minWidth: 120, maxWidth: 120 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: 110 }}>
                          <button
                            onClick={() => onView(inv)}
                            style={{
                              background: G.accentBg,
                              color: G.accent,
                              border: `1.8px solid ${G.accent}40`,
                              borderRadius: 6,
                              padding: "6px 10px",
                              fontSize: 12,
                              cursor: "pointer",
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              width: "100%",
                              transition: "all 0.15s"
                            }}
                            onMouseEnter={e => {
                              e.target.style.background = G.accent;
                              e.target.style.color = G.accentFg;
                              e.target.style.border = "1.8px solid transparent";
                            }}
                            onMouseLeave={e => {
                              e.target.style.background = G.accentBg;
                              e.target.style.color = G.accent;
                              e.target.style.border = `1.8px solid ${G.accent}40`;
                            }}
                          >
                            View
                          </button>
                          
                          <button
                            onClick={() => handleAddPayment(inv)}
                            disabled={!hasPaymentTracking}
                            style={{
                              background: hasPaymentTracking ? "#FFFBEB" : G.surface2,
                              color: hasPaymentTracking ? "#A16207" : G.muted,
                              border: hasPaymentTracking ? "1.8px solid rgba(234, 179, 8, 0.35)" : `1px solid ${G.border}`,
                              borderRadius: 6,
                              padding: "6px 10px",
                              fontSize: 12,
                              cursor: hasPaymentTracking ? "pointer" : "not-allowed",
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              width: "100%",
                              opacity: hasPaymentTracking ? 1 : 0.4,
                              filter: hasPaymentTracking ? 'none' : 'grayscale(100%)',
                              transition: "all 0.15s"
                            }}
                            onMouseEnter={e => {
                              if (hasPaymentTracking) {
                                e.target.style.background = "#EAB308";
                                e.target.style.color = "#FFFFFF";
                                e.target.style.border = "1.8px solid transparent";
                              }
                            }}
                            onMouseLeave={e => {
                              if (hasPaymentTracking) {
                                e.target.style.background = "#FFFBEB";
                                e.target.style.color = "#A16207";
                                e.target.style.border = "1.8px solid rgba(234, 179, 8, 0.35)";
                              }
                            }}
                          >
                            Add Payment
                          </button>
                          
                          <button
                            onClick={() => handleDeleteClick(inv)}
                            disabled={!hasCloudDB}
                            style={{
                              background: hasCloudDB ? "#FEF2F2" : G.surface2,
                              color: hasCloudDB ? "#F87171" : G.muted,
                              border: hasCloudDB ? "1.8px solid rgba(248, 113, 113, 0.35)" : `1px solid ${G.border}`,
                              borderRadius: 6,
                              padding: "6px 10px",
                              fontSize: 12,
                              cursor: hasCloudDB ? "pointer" : "not-allowed",
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              width: "100%",
                              opacity: hasCloudDB ? 1 : 0.4,
                              filter: hasCloudDB ? 'none' : 'grayscale(100%)',
                              transition: "all 0.15s"
                            }}
                            onMouseEnter={e => {
                              if (hasCloudDB) {
                                e.target.style.background = "#EF4444";
                                e.target.style.color = "#FFFFFF";
                                e.target.style.border = "1.8px solid transparent";
                              }
                            }}
                            onMouseLeave={e => {
                              if (hasCloudDB) {
                                e.target.style.background = "#FEF2F2";
                                e.target.style.color = "#F87171";
                                e.target.style.border = "1.8px solid rgba(248, 113, 113, 0.35)";
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div style={{ textAlign: "center", paddingTop: 14 }}>
              <button
                onClick={() => setDisplayLimit(prev => prev + 50)}
                style={{
                  background: G.accent,
                  color: G.accentFg,
                  border: "none",
                  borderRadius: 7,
                  padding: "10px 24px",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif"
                }}
              >
                Load Next 50 Invoices ({sorted.length - displayLimit} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}